import type { ReactNode } from 'react';
import React, { useEffect, memo, useState, useSyncExternalStore } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import { Flex, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlaskit/theme/constants';
import { SpotlightTarget, SpotlightTransition, SpotlightManager } from '@atlaskit/onboarding';
import { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Tooltip from '@atlaskit/tooltip';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { useReactProps_DO_NOT_USE } from '@atlassian/embedded-confluence/useReactProps';
import type { IframePassThroughProps } from '@atlassian/embedded-confluence/IframeElementType';

import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useRouteName } from '@confluence/route-manager';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import {
	AIFloatingContextMenu,
	AISmartButtonStateHandler,
	AISmartButtonWrapper,
} from '@confluence/ai-floating-context-menu';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { fg } from '@confluence/feature-gating';
import { PageHeaderLoomEntryPoint } from '@confluence/page-header-loom-button';
import { useQuickstartEligible, useQuickstartState } from '@confluence/onboarding-quickstart';
import {
	useSidebarControlState,
	useSidebarControlExpandCollapsedState,
	useObjectSidebarActions,
} from '@confluence/object-sidebar-api';
import { useSpaceId } from '@confluence/space-utils';
import { useIsLoomDisabled } from '@confluence/loom-utils';
import { LoadableLazy } from '@confluence/loadable';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';
import { usePreviewPanelWidth } from '@confluence/preview-panel-api';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics';
import { expVal } from '@confluence/feature-experiments';
import { OBJECT_SIDEBAR_SPOTLIGHT_TARGET } from '@confluence/modernize-changeboarding/entry-points/spotlightTargets';
import { useAIRovoButtonEnablement } from '@confluence/ai-floating-context-menu/entry-points/useAIRovoButtonEnablement';

import { OBJECT_SIDEBAR_TOUR_TARGETS } from './objectSidebarTourTargets';
import { DetailsPanelButton } from './sidebar-buttons/DetailsPanelButton';
import { WorkflowsPanelButton } from './sidebar-buttons/WorkflowsPanelButton';
import { ObjectSidebarReactionsPicker } from './sidebar-buttons/ObjectSidebarReactionsPicker';
import { AudioButton } from './sidebar-buttons/AudioButton';
import { CommentPanelButton } from './sidebar-buttons/CommentPanelButton';
import { FeedbackCollectorButton } from './sidebar-buttons/FeedbackCollectorButton';
import { OBJECT_SIDEBAR_CONTROL_METRIC } from './perf.config';

const ObjectSidebarSpotlightTour = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ObjectSidebarSpotlightTour" */ './ObjectSidebarSpotlightTour'
			)
		).ObjectSidebarSpotlightTour,
});

const objectSidebarStyles = css({
	position: 'fixed',
	right: 0,
	bottom: 0,
	zIndex: layers.dialog(),
	pointerEvents: 'none',
	// The selector below ensures the elements behind the empty space taken up by the container remain clickable/responsive to mouse events
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'& > *': {
		pointerEvents: 'auto',
	},
});

const objectSidebarPaddingStyles = css({
	paddingRight: `${token('space.300')}`,
	paddingBottom: `${token('space.100')}`,
});

const databaseObjectSidebarPaddingStyles = css({
	paddingRight: `${token('space.100')}`,
	paddingBottom: `${token('space.100')}`,
});

const viewModeStylesWithLegacyFooter = css({
	//z index is set to 1 to ensure that the object sidebar is below the inline comment component
	zIndex: 1,
});

const viewModeStylesWithNewFooter = css({
	//z index is set to 2 to ensure that the object sidebar is below the inline comment component but above the "Add a comment" button
	zIndex: 2,
});

const quickstartSpacing = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '72px',
});

/**
 * If you change width here, then make sure to update leftOffset in
 *  annotation-provider-inline-comments/src/CommentSidebar.tsx
 */
const objectSidebarContainerStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: '64px',
	width: '48px',
	marginLeft: 'auto',
});

/**
 * If you change width here, then make sure to update leftOffset in
 *  annotation-provider-inline-comments/src/CommentSidebar.tsx
 */
const squircleObjectSidebarContainerStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: '12px',
	width: '48px',
	marginLeft: 'auto',
});

const collapsibleObjectSidebarContainerStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: '64px',
});

const collapsibleSquircleObjectSidebarContainerStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: '12px',
});

const collapsibleContainerStyles = css({
	width: `${token('space.600')}`,
	marginLeft: 'auto',
});

const collapsibleBottomSidebarStyles = xcss({
	paddingTop: 'space.100',
	marginBottom: 'space.050',
});

const rovoButtonCollapsibleBottomSidebarStyles = css({
	paddingTop: token('space.100'),
	marginBottom: token('space.200'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '1px',
});

const collapsibleToggleStyles = css({
	margin: 'auto',
	width: `${token('space.400')}`,
});

const dividerStyle = xcss({
	borderBottom: `2px solid ${token('color.border')}`,
	display: 'inline-block',
	width: '16px',
	height: '1px',
});

const bottomSidebarStyles = css({
	marginTop: token('space.100'),
	marginBottom: token('space.050'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '1px',
});

const rovoButtonBottomSidebarStyles = css({
	marginTop: token('space.100'),
	/**
	 * There's an additional bottom space.100 in the parent container
	 * We want space.300 around both sides
	 */
	marginBottom: token('space.200'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '1px',
});

const hiddenStyles = css({
	display: 'none',
});

const i18n = defineMessages({
	expandToggleLabel: {
		id: 'object-sidebar-components.expand-toggle-label',
		defaultMessage: 'Expand action bar',
		description: 'Label for button to expand the floating action bar',
	},
	collapseToggleLabel: {
		id: 'object-sidebar-components.collapse-toggle-label',
		defaultMessage: 'Collapse action bar',
		description: 'Label for button to collapse the floating action bar',
	},
});

const TRACKED_NETWORK_EVENTS: (keyof WindowEventMap)[] = ['load', 'online', 'offline'];

const isOnlineSubscribe: Parameters<typeof useSyncExternalStore<boolean>>[0] = (callback) => {
	TRACKED_NETWORK_EVENTS.forEach((event) => {
		window.addEventListener(event, callback);
	});
	return () => {
		TRACKED_NETWORK_EVENTS.forEach((event) => {
			window.removeEventListener(event, callback);
		});
	};
};

const isOnlineGetSnapshot: Parameters<typeof useSyncExternalStore<boolean>>[1] = () =>
	navigator.onLine;

// During SSR rendering, we can't check the navigator status, so instead we'll assume the user is online
const isOnlineGetServerSnapshot: Parameters<typeof useSyncExternalStore<boolean>>[2] = () => true;

const IsOnlineWrapper = ({ children }: { children: (isOnline: boolean) => ReactNode }) => {
	const isOnline = useSyncExternalStore(
		isOnlineSubscribe,
		isOnlineGetSnapshot,
		isOnlineGetServerSnapshot,
	);
	return children(isOnline);
};
const ConditionalIsOnlineWrapper = ({
	condition,
	children,
}: {
	condition: boolean;
	children: (isOnline: boolean) => ReactNode;
}) => {
	if (condition) {
		{
			/* Conditionally use the hook if the condition (experiment gate) is true */
		}
		return <IsOnlineWrapper>{children}</IsOnlineWrapper>;
	}
	return children(true);
};

export type ObjectSidebarControlProps = {
	hasCommentsButton?: boolean;
	hasDetailsButton?: boolean;
	hasLoomButton?: boolean;
	hasAiButton?: boolean;
	hasReactionsButton?: boolean;
	hasAudioButton?: boolean;
	contentId: string;
	spaceKey?: string;
	contentType?: string | null;
	contentTitle?: string | null;
	lastModifiedDate?: string | null;
	pageCommentsSectionInView?: boolean;
	reactionsPickerPreventOverflowOptions?: Record<string, any>;
	isLoomAIScriptExperimentEnabled?: boolean;
};

export const ObjectSidebarControl = memo(
	({
		hasCommentsButton,
		hasDetailsButton,
		hasLoomButton,
		hasAiButton,
		hasReactionsButton,
		hasAudioButton,
		contentId,
		spaceKey,
		contentType,
		contentTitle,
		lastModifiedDate,
		pageCommentsSectionInView,
		reactionsPickerPreventOverflowOptions,
		isLoomAIScriptExperimentEnabled = false,
	}: ObjectSidebarControlProps) => {
		const { isFullScreenEmbedView } = useReactProps_DO_NOT_USE<IframePassThroughProps>() || {};

		const { formatMessage } = useIntl();
		const [actionBarIsHovered, setActionBarIsHovered] = useState(false);
		const sidebarControlState = useSidebarControlState();
		const sidebarControlExpandCollapsedState = useSidebarControlExpandCollapsedState();
		const {
			setSidebarControlState,
			toggleObjectSidebarControl,
			setSidebarControlExpandCollapsedState,
		} = useObjectSidebarActions();

		const pageMode = useGetPageMode();
		const spaceId = useSpaceId();
		const isLoomDisabled = useIsLoomDisabled();

		const rightSidebarContext = useRightSidebarContext();
		const { width: globalSidebarWidth } = rightSidebarContext.getCurrent() || {};
		const { width: previewPanelWidth } = usePreviewPanelWidth();

		const isRovoButtonEnabled = useAIRovoButtonEnablement();

		const isOnPDFExportPageRouteArgs = {
			selector: (routeName: string | undefined) => {
				if (!routeName) {
					return false;
				}
				return routeName === PDF_EXPORT_PAGE.name;
			},
		};

		const isOnPDFExportPageRoute = useRouteName(isOnPDFExportPageRouteArgs);

		const isLivePage = useIsCurrentPageLive();
		const renderServerPlaceholder = useRenderServerPlaceholder();

		const { isDismissed } = useQuickstartState();
		const { isQuickstartEligible } = useQuickstartEligible();
		const isQuickStartPillShowing = !isDismissed && isQuickstartEligible();

		// whenever the contentID changes we needed to reset the action bar to be expanded
		useEffect(() => {
			setSidebarControlExpandCollapsedState('expanded');
		}, [contentId, setSidebarControlExpandCollapsedState]);

		useEffect(() => {
			setSidebarControlState('shown');
			setSidebarControlExpandCollapsedState('expanded');

			return () => {
				setSidebarControlExpandCollapsedState('collapsed');
			};
		}, [setSidebarControlState, setSidebarControlExpandCollapsedState]);

		const sidebarIsExpanded =
			sidebarControlExpandCollapsedState === 'expanded' || renderServerPlaceholder;

		// Collapsed actions are rendered when collapsed. These can be used e.g. to enable keyboard shortcuts while collapsed.
		const collapsedActions = [];

		const primaryActions: JSX.Element[] = [];
		if (hasCommentsButton && fg('confluence-frontend-comments-panel')) {
			primaryActions.push(<CommentPanelButton contentId={contentId} />);
			if (!sidebarIsExpanded) {
				collapsedActions.push(<CommentPanelButton contentId={contentId} collapsed />);
			}
		}
		if (hasDetailsButton) {
			primaryActions.push(<DetailsPanelButton contentId={contentId} />);
			if (!sidebarIsExpanded) {
				collapsedActions.push(<DetailsPanelButton contentId={contentId} collapsed />);
			}
		}
		if (fg('document_workflows')) {
			primaryActions.push(<WorkflowsPanelButton />);
		}

		const secondaryActions: JSX.Element[] = [];

		if (hasReactionsButton) {
			secondaryActions.push(
				<ConditionalIsOnlineWrapper
					condition={expVal('platform_editor_offline_editing_web', 'isEnabled', false)}
				>
					{(isOnline) => (
						<ObjectSidebarReactionsPicker
							contentId={contentId}
							spaceId={spaceId ?? ''}
							contentSubType={pageMode}
							contentType={contentType ?? ''}
							reactionsPickerPreventOverflowOptions={reactionsPickerPreventOverflowOptions}
							isDisabled={!isOnline}
						/>
					)}
				</ConditionalIsOnlineWrapper>,
			);
		}

		if (hasAudioButton) {
			secondaryActions.push(
				<AudioButton
					contentId={contentId}
					contentType={contentType}
					spaceKey={spaceKey}
					contentTitle={contentTitle}
				/>,
			);
		}

		if (hasLoomButton && !isLoomDisabled) {
			secondaryActions.push(
				<PageHeaderLoomEntryPoint
					contentId={contentId}
					crossSellPopupPlacement="left-end"
					spaceKey={spaceKey ?? ''}
					contentType={contentType ?? ''}
					isCircle={!isRovoButtonEnabled}
					testId="sidebar-loom-button"
					source="contentWrapper"
					isLoomAIScriptExperimentEnabled={isLoomAIScriptExperimentEnabled ?? false}
				/>,
			);
		}

		secondaryActions.push(<FeedbackCollectorButton contentId={contentId} />);

		if (isOnPDFExportPageRoute) {
			return null;
		}

		const globalSidebarMargin =
			pageMode === PageMode.VIEW && globalSidebarWidth ? globalSidebarWidth : 0;
		const previewPanelMargin =
			pageMode === PageMode.VIEW && previewPanelWidth ? previewPanelWidth : 0;
		const shouldRenderWhenEmbedded = isEmbeddedConfluence_DO_NOT_USE()
			? isFullScreenEmbedView && fg('confluence_support_content_wrapper_in_ep_m1')
			: !isEmbeddedConfluence_DO_NOT_USE();

		// Remove <SpotlightManager> on top of `ObjectSidebarSpotlightTour`  when FG `cc_modernize_ga_changeboarding` is enabled
		// This will allow the `ModernizeChangeboarding` component to have combined spollight tour managed by the <SpotlightManager> in `PageLayout`
		// We still keep the <SpotlightManager> in place for existing Live Doc changeboarding EAP and Open beta users.
		// We will remove `SpotlightManager` in this file when FG `cc_modernize_ga_changeboarding` is rolled out
		const SpotlightTourWrapper = fg('cc_modernize_ga_changeboarding')
			? React.Fragment
			: SpotlightManager;

		if (fg('confluence_frontend_collapsible_action_bar')) {
			// If there's no AI button, when we collapse the action bar, we need to render an expand icon within the
			// existing action bar container. If there is an AI button, hovering over it should render the expand icon
			const shouldRenderIntegratedExpandButton = !hasAiButton && !sidebarIsExpanded;
			// We always render the toggle component into the DOM, but we change its visibility based on:
			// If the action bar is expanded: show on hover (regardless of AI button)
			// If the action bar is collapsed: always show if AI button present, otherwise never show
			const shouldDisplayHoverToggle =
				(sidebarIsExpanded && actionBarIsHovered) || (hasAiButton && !sidebarIsExpanded);

			const handleMouseEnter = () => {
				setActionBarIsHovered(true);
			};

			const handleMouseLeave = () => {
				setActionBarIsHovered(false);
			};

			const onToggleClick = () => {
				toggleObjectSidebarControl();
				// need to make sure the hover (dis)appears after clicking it
				setActionBarIsHovered(!sidebarIsExpanded);
			};

			const toggleLabel = sidebarIsExpanded
				? formatMessage(i18n.collapseToggleLabel)
				: formatMessage(i18n.expandToggleLabel);

			const integratedExpandButton = (
				<Tooltip position="left" content={formatMessage(i18n.expandToggleLabel)} hideTooltipOnClick>
					<IconButton
						appearance="subtle"
						icon={(iconProps) => <ChevronUpIcon {...iconProps} size="small" />}
						label={toggleLabel}
						onClick={toggleObjectSidebarControl}
						shape="circle"
						testId="integrated-expand-button"
					/>
				</Tooltip>
			);

			return (
				<>
					<AISmartButtonStateHandler
						spaceKey={spaceKey ?? ''}
						contentId={contentId}
						contentType={contentType ?? ''}
						lastModifiedDate={lastModifiedDate}
						contentTitle={contentTitle ?? ''}
					/>
					{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
					<div
						css={[
							objectSidebarStyles,
							(contentType === 'page' ||
								contentType === 'blogpost' ||
								pageMode === PageMode.VIEW) &&
								objectSidebarPaddingStyles,
							contentType === 'database' && databaseObjectSidebarPaddingStyles,
							pageMode === PageMode.VIEW &&
								!fg('confluence-frontend-redesigned-footer-comment') &&
								viewModeStylesWithLegacyFooter,
							pageMode === PageMode.VIEW &&
								fg('confluence-frontend-redesigned-footer-comment') &&
								viewModeStylesWithNewFooter,
							isQuickStartPillShowing && quickstartSpacing,
							!renderServerPlaceholder && sidebarControlState === 'hidden' && hiddenStyles,
						]}
						data-testid="object-sidebar-container"
						data-vc="object-sidebar-container"
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							marginRight: globalSidebarMargin + previewPanelMargin,
						}}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
					>
						{primaryActions.length + secondaryActions.length > 0 &&
							shouldRenderWhenEmbedded &&
							fg('confluence_frontend_object_sidebar') && (
								<SpotlightTourWrapper>
									<SpotlightTarget name={OBJECT_SIDEBAR_TOUR_TARGETS.OBJECT_SIDEBAR_CONTROL}>
										<div css={collapsibleContainerStyles}>
											<Tooltip position="left" content={toggleLabel} hideTooltipOnClick>
												<div
													css={collapsibleToggleStyles}
													style={{
														paddingBottom: sidebarIsExpanded ? `${token('space.100')}` : undefined,
														visibility: shouldDisplayHoverToggle ? 'visible' : 'hidden',
													}}
													data-testid="collapsible-hover-toggle"
												>
													<IconButton
														appearance="subtle"
														icon={(iconProps) =>
															sidebarIsExpanded ? (
																<ChevronDownIcon {...iconProps} size="small" />
															) : (
																<ChevronUpIcon {...iconProps} size="small" />
															)
														}
														label={toggleLabel}
														onClick={onToggleClick}
														shape="circle"
														testId="collapsible-hover-button"
													/>
												</div>
											</Tooltip>
											<SpotlightTarget name={OBJECT_SIDEBAR_SPOTLIGHT_TARGET}>
												<div>
													{sidebarIsExpanded || renderServerPlaceholder ? (
														<Flex
															xcss={
																isRovoButtonEnabled
																	? collapsibleSquircleObjectSidebarContainerStyles
																	: collapsibleObjectSidebarContainerStyles
															}
															direction="column"
															gap="space.100"
															justifyContent="center"
															alignItems="center"
															testId="object-sidebar-control"
														>
															{primaryActions}
															{secondaryActions.length > 0 && <Box xcss={dividerStyle} />}
															{secondaryActions}
														</Flex>
													) : (
														<>{collapsedActions}</>
													)}
												</div>
											</SpotlightTarget>
											{shouldRenderIntegratedExpandButton ? (
												<Flex
													xcss={
														isRovoButtonEnabled
															? collapsibleSquircleObjectSidebarContainerStyles
															: collapsibleObjectSidebarContainerStyles
													}
													direction="column"
													gap="space.100"
													justifyContent="center"
													alignItems="center"
													testId="object-sidebar-control"
												>
													{integratedExpandButton}
												</Flex>
											) : null}
										</div>
									</SpotlightTarget>
									<SpotlightTransition>
										<ObjectSidebarSpotlightTour contentId={contentId} />
									</SpotlightTransition>
								</SpotlightTourWrapper>
							)}
						{hasAiButton &&
							shouldRenderWhenEmbedded &&
							(isRovoButtonEnabled ? (
								<div css={rovoButtonCollapsibleBottomSidebarStyles}>
									<AISmartButtonWrapper
										contentId={contentId}
										contentType={contentType ?? ''}
										pageMode={pageMode}
									/>
								</div>
							) : (
								<Box xcss={collapsibleBottomSidebarStyles}>
									<AIFloatingContextMenu
										spaceKey={spaceKey ?? ''}
										contentId={contentId}
										contentType={contentType ?? ''}
										lastModifiedDate={lastModifiedDate}
										pageCommentsSectionInView={pageCommentsSectionInView}
										isLivePage={isLivePage}
										isInObjectSideBar
									/>
								</Box>
							))}
					</div>
					<PageSegmentLoadEnd metric={OBJECT_SIDEBAR_CONTROL_METRIC} />
				</>
			);
		}

		return (
			<>
				<AISmartButtonStateHandler
					spaceKey={spaceKey ?? ''}
					contentId={contentId}
					contentType={contentType ?? ''}
					lastModifiedDate={lastModifiedDate}
					contentTitle={contentTitle ?? ''}
				/>
				<div
					css={[
						objectSidebarStyles,
						pageMode === PageMode.VIEW &&
							!fg('confluence-frontend-redesigned-footer-comment') &&
							viewModeStylesWithLegacyFooter,
						pageMode === PageMode.VIEW &&
							fg('confluence-frontend-redesigned-footer-comment') &&
							viewModeStylesWithNewFooter,
						isQuickStartPillShowing && quickstartSpacing,
						!renderServerPlaceholder && sidebarControlState === 'hidden' && hiddenStyles,
					]}
					data-testid="object-sidebar-container"
					data-vc="object-sidebar-container"
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						marginRight: globalSidebarMargin + previewPanelMargin,
					}}
				>
					{primaryActions.length + secondaryActions.length > 0 &&
						shouldRenderWhenEmbedded &&
						fg('confluence_frontend_object_sidebar') && (
							<SpotlightTourWrapper>
								<SpotlightTarget
									name={
										fg('cc_modernize_ga_changeboarding')
											? OBJECT_SIDEBAR_SPOTLIGHT_TARGET
											: OBJECT_SIDEBAR_TOUR_TARGETS.OBJECT_SIDEBAR_CONTROL
									}
								>
									<Flex
										xcss={
											isRovoButtonEnabled
												? squircleObjectSidebarContainerStyles
												: objectSidebarContainerStyles
										}
										direction="column"
										gap="space.100"
										justifyContent="center"
										alignItems="center"
										testId="object-sidebar-control"
									>
										{primaryActions}
										{secondaryActions.length > 0 && <Box xcss={dividerStyle} />}
										{secondaryActions}
									</Flex>
								</SpotlightTarget>
								<SpotlightTransition>
									<ObjectSidebarSpotlightTour contentId={contentId} />
								</SpotlightTransition>
							</SpotlightTourWrapper>
						)}
					{hasAiButton && shouldRenderWhenEmbedded && (
						<div css={[isRovoButtonEnabled ? rovoButtonBottomSidebarStyles : bottomSidebarStyles]}>
							{isRovoButtonEnabled ? (
								<AISmartButtonWrapper
									contentId={contentId}
									contentType={contentType ?? ''}
									pageMode={pageMode}
								/>
							) : (
								<AIFloatingContextMenu
									spaceKey={spaceKey ?? ''}
									contentId={contentId}
									contentType={contentType ?? ''}
									lastModifiedDate={lastModifiedDate}
									pageCommentsSectionInView={pageCommentsSectionInView}
									isLivePage={isLivePage}
									isInObjectSideBar
								/>
							)}
						</div>
					)}
				</div>
				<PageSegmentLoadEnd metric={OBJECT_SIDEBAR_CONTROL_METRIC} />
			</>
		);
	},
);
